import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { map, filter, debounce } from "lodash";
import PropTypes from "prop-types";
import { getReferralWorklistFilter, getTotalNumberOfReferral } from "app/main/referralWorklist/reducers/referralWorklist.reducers";
import { getReferralWorkflowStateSettings, getViewableTriageCategorySettings, getReportingUseHangfireQueuing } from "app/auth/store/reducers/system-configuration";
import { updateReferralWorkListFilterTags } from "app/main/referralWorklist/actions/referralWorklist.actions";
import { handleHangfireExportResponse } from "app/main/reportExport/actions/reportExport.actions";
import { showMessage } from "app/store/actions";
import useReferralFieldSettings from "../../referrals/hooks/useReferralFieldSettings";

const useReferralSearch = ({
  params,
  orgUnitId,
  searchAPI,
  exportAPI,
  updateFilterTags = updateReferralWorkListFilterTags,
}) => {
  const dispatch = useDispatch();
  const viewableTriageCategorySettings = useSelector(getViewableTriageCategorySettings);
  const triageCategoryChoices = map(viewableTriageCategorySettings, x => x.triageCategory);
  const referralWorkflowSettings = useSelector(getReferralWorkflowStateSettings);
  const referralStatusChoices = map(filter(referralWorkflowSettings, x => x.isEnabled === true), setting => setting.workflowState);
  const presetFilter = useSelector(getReferralWorklistFilter);
  const reportingUseHangfireQueuing = useSelector(getReportingUseHangfireQueuing);
  const totalNumberOfReferrals = useSelector(getTotalNumberOfReferral);
  const { fieldLabels: referralFieldLabels } = useReferralFieldSettings();

  const debouncedSearchRef = useRef(); // Holds the stable debounced function (created once, never recreated)
  const searchCallbackRef = useRef(); // Holds the latest search callback closure so the debounced function always uses current values
  const pendingResolvesRef = useRef([]); // Accumulates all pending Promise resolve callbacks across debounced calls
  // Keep the callback ref updated with latest closure values on every render
  searchCallbackRef.current = (searchParams, page, pageSize, reload) => {
    // Capture and drain all pending resolve callbacks before the async dispatch.
    // This ensures that even if new onSearch calls arrive while we're awaiting,
    // we resolve exactly the promises that were queued before this invocation fired.
    const resolves = pendingResolvesRef.current;
    pendingResolvesRef.current = [];
    dispatch(searchAPI(searchParams, page || 1, pageSize || 14, reload))
      .then(res => {
        if (res) {
          dispatch(updateFilterTags(res.payload?.searchParams, referralFieldLabels));
        }
        resolves.forEach(resolve => resolve(res));
      });
  };

  // Create debounced search to help prevent spamming API requests - delegates to the ref for latest values
  useEffect(() => {
    debouncedSearchRef.current = debounce((...args) => {
      searchCallbackRef.current(...args);
    }, 300);
    return () => debouncedSearchRef.current?.cancel();
  }, []);

  const getParameters = parameters => {
    if (!parameters) {
      return {
        searchParams: null,
        initialValues: null,
      };
    }

    const referralFilters = {
      ...parameters,
      orgUnitId,
      dateRange: [params.startDate, params.endDate],
    };

    return ({
      searchParams: referralFilters,
      orgUnitId,
      initialValues: {
        referralFilters,
      },
    });
  };

  const onSearch = (newParams, page, pageSize, reload = false) => new Promise(resolve => {
    pendingResolvesRef.current.push(resolve);
    const { searchParams } = getParameters(newParams);
    debouncedSearchRef.current(searchParams, page, pageSize, reload);
  });

  const onExport = async () => {
    if (!exportAPI) return null;

    const { searchParams } = getParameters(params);

    try {
      /*
       * background exporting: false
       */
      if (!reportingUseHangfireQueuing) {
        const response = await dispatch(exportAPI(searchParams, false, totalNumberOfReferrals));

        if (!response.ok) {
          const errorMessage = await response.text();
          dispatch(showMessage({ message: errorMessage, variant: "error" }));
          return null;
        }
        return null;
      }

      /*
       * background exporting: true
       */
      return handleHangfireExportResponse(
        dispatch(exportAPI(searchParams, true, totalNumberOfReferrals)),
        dispatch,
      );
    } catch (error) {
      dispatch(showMessage({ message: error, variant: "error" }));
      return null;
    }
  };

  const { searchParams, initialValues } = getParameters(params);

  return ({
    searchParams,
    initialValues,
    referralStatusChoices,
    triageCategoryChoices,
    presetFilter,
    referralWorkflowSettings,
    triageCategorySettings: viewableTriageCategorySettings,
    onSearch,
    onExport,
  });
};

useReferralSearch.propTypes = {
  params: PropTypes.shape({}).isRequired, // params is an object
  searchAPI: PropTypes.func.isRequired,
  exportAPI: PropTypes.func,
};

export default useReferralSearch;
